import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Actions } from 'src/app/models';
import { EmailFooter, WorkflowActivityEmailFooter } from 'src/app/models/email-footer';
import { ClientService, SecurityService, SystemService, Utilities, WorkflowContextService } from 'src/app/services';
import { EmailFooterEditorComponent } from '../email-footer-editor/email-footer-editor.component';
import { ModalConfirmComponent } from 'src/app/components/system/modal-confirm/modal-confirm.component';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'wm-email-footers-list',
    templateUrl: './email-footers-list.component.html',
    styleUrl: './email-footers-list.component.css',
    standalone: false
})
export class EmailFootersListComponent implements OnInit, OnDestroy {
  @ViewChild('emailFooterEditor', { static: false }) emailFooterEditor: EmailFooterEditorComponent;
  @ViewChild('emailFooterEditorModal') editModal: ModalConfirmComponent;
  @ViewChild('deleteEmailFooterModal') deleteModal: ModalConfirmComponent;
  @ViewChild('emailFooterUsageModal') emailFooterUsageModal: ModalConfirmComponent;
  
  clientSubscribe: Subscription;
  canManageEmailFooters = false;
  canEditGlobal = false;
  form: UntypedFormGroup;

  emailFooters: EmailFooter[];
  editingEmailFooter: EmailFooter = null;
  deletingEmailFooterId: string;
  workflowActivities: WorkflowActivityEmailFooter[] = [];
  loadingWorkflowActivities = false;
  clientMode = true;
  
  constructor(
    private _context: WorkflowContextService,
    private _securitySvc: SecurityService,
    private _clientSvc: ClientService,
    private _systemSvc: SystemService,
    private _toastr: ToastrService,
    private _fb: UntypedFormBuilder,
    private _route: ActivatedRoute
  ) {}

  ngOnInit() {
    this._route.data.subscribe(d => {
      if (d.clientMode === false) {
        this.clientMode = d.clientMode;
      }

      // create an empty form group to be populated by children
      this.form = this._fb.group({});

      if (this.clientMode) {
        if (this._context.client) {
          this.getEmailFooters();
        } else {
          this.clientSubscribe = this._context.client$.subscribe(res => {
            this.getEmailFooters();
          });
        }

        this._securitySvc
          .isLoginEntitled(Actions.MANAGE_EMAIL_FOOTERS)
          .subscribe(r => {
            this.canManageEmailFooters = r;
          });
      } else {

        this._securitySvc
          .isLoginEntitled(Actions.DO_ANYTHING)
          .subscribe(r => {
            this.canEditGlobal = r;
          });

        this._systemSvc
          .getGlobalEmailFooters()
          .subscribe(emailFooters => {
            this.emailFooters = emailFooters || [];
          });
      }
    });
  }

  ngOnDestroy() {
    if (this.clientSubscribe) {
      this.clientSubscribe.unsubscribe();
    }
  }

  getEmailFooters() {
    this._clientSvc
    .getEmailFooters(this._context.client.id)
    .subscribe(emailFooters => {
      this.emailFooters = emailFooters || [];
    });
  }

  createEmailFooter() {
    let newEmailFooter = this.clientMode 
    ? new EmailFooter({
      id: Utilities.generateId(),
      clientId: this._context.client.id,
      label: '',
      htmlContent: '',
      isEnabled: true
    })
    : new EmailFooter({
      id: Utilities.generateId(),
      label: '',
      htmlContent: '',
      isEnabled: true
    });

    this.editingEmailFooter = newEmailFooter;

    this.editModal.open();
  }

  editEmailFooter(emailFooter: EmailFooter) {
    this.editingEmailFooter = JSON.parse(JSON.stringify(emailFooter));

    this.editModal.open();
  }

  cancelEditing() {
    this.editingEmailFooter = null;
  }

  saveEmailFooter(closeModal = false) {
    this._systemSvc
      .saveEmailFooter(this.editingEmailFooter)
      .subscribe(savedEmailFooter => {
        let emailFooterToUpdate = this.emailFooters.find(
          ef => ef.id === this.editingEmailFooter.id
        );

        if (emailFooterToUpdate) {
          const objIndex = this.emailFooters.findIndex(ef => ef.id === emailFooterToUpdate.id);
              
          savedEmailFooter.inUse = emailFooterToUpdate.inUse;

          this.emailFooters[objIndex] = savedEmailFooter;      
        } else {
          this.emailFooters.push(savedEmailFooter);
        }
        
        this.editingEmailFooter = null;

        this.emailFooters = this.emailFooters.sort((a, b) => (a.label > b.label ? 1 : -1))

        if (closeModal) {
          this.editModal.cancel();
        }

        this._toastr.success('Email Footer successfully saved.', null);
      });
  }

  deleteEmailFooter(emailFooterId: string, confirmed = false) {
    if (confirmed) {
      this._systemSvc.deleteEmailFooter(emailFooterId).subscribe(success => {
        if (success) {
          this.emailFooters = this.emailFooters.filter(
            ef => ef.id !== emailFooterId
          );

          this.deleteModal.cancel();
        } else {
          this._toastr.error(
            'Something went wrong when deleting the Email Footer. Please contact support if this error persists.',
            'Email Footer was not deleted.',
            { disableTimeOut: true }
          );
        }
      });
    } else {
      this.deletingEmailFooterId = emailFooterId;

      this.deleteModal.open();
    }
  }

  changeEnabledStatus(id: string, enable: boolean) {
    let emailFooterToChange = this.emailFooters.find(
      ef => ef.id === id
    );

    if (emailFooterToChange) {
      this.editingEmailFooter = {...emailFooterToChange};

      this.editingEmailFooter.isEnabled = enable;

      this.saveEmailFooter();
    }
  }

  viewFooterUsage(emailFooterId: string) {
    this.loadingWorkflowActivities = true;

    this.workflowActivities = [];

    this.emailFooterUsageModal.open();

    this._systemSvc.getEmailFooterActivities(emailFooterId)
      .subscribe(activities => {
        if (activities && activities.length > 0) {
          this.workflowActivities = activities;
        }

        this.loadingWorkflowActivities = false;
      });
  }
}
